import React, { useState, useMemo, useCallback, memo } from 'react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  LineElement, PointElement, Title, Tooltip, Legend, ArcElement,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

import DashboardHeader from './components/DashboardHeader';
import FilterModal from './components/FilterModal';
import MetricsCards from './components/MetricsCards';
import ChartCard from './components/ChartCard';
import QuickStats from './components/QuickStats';
import RepeatRateChart from './components/RepeatRateChart';
import SectionLabel from './components/SectionLabel';
import LoadingOverlay from './components/LoadingOverlay';
import UserListModal from './components/UserListModal';
import OrderListModal from './components/OrderListModal';
import ClickableBarChart from './components/ClickableBarChart';
import ClickableLineChart from './components/ClickableLineChart';
import { useAnalyticsData } from './hooks/useAnalyticsData';
import { useFilterHandlers } from './hooks/useFilterHandlers';
import { useChartHandlers } from './hooks/useChartHandlers';
import { chartOptions, pieOptions, getCompareYearOptions } from './utils/chartConfig';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, ArcElement);

// Stable empty modal state — avoids new object on every render
const CLOSED_MODAL = { isOpen: false, users: [], orders: [], month: '', year: '' };

// ── City options memoized outside component ───────────────────
const cityBarOptions = { ...pieOptions, responsive: true, maintainAspectRatio: false };

// ── Section components — each only re-renders when its data changes ──

const OperationsSection = memo(({ dashboardData, chartHandlers, filters, successRate, upiPct }) => (
  <>
    <SectionLabel color="bg-indigo-500" title="Operations" hint="Click any chart bar to drill into orders" />

    <div className="col-span-12 lg:col-span-3 flex flex-col gap-4">
      <ChartCard title="Order Status" subtitle="By current filter" insight={`${successRate}% delivered`}>
        <ClickableBarChart data={dashboardData.statusChart} options={pieOptions} onClick={chartHandlers.handleStatusChartClick} />
      </ChartCard>
      <ChartCard title="Payment Methods" subtitle="UPI vs COD" insight={`UPI ${upiPct}%`}>
        <ClickableBarChart data={dashboardData.paymentChart} options={pieOptions} onClick={chartHandlers.handlePaymentChartClick} />
      </ChartCard>
    </div>

    <ChartCard title="Monthly Performance" subtitle={`Orders & Revenue · ${filters.year}`} className="col-span-12 lg:col-span-6" insight="Click month to drill down">
      <ClickableBarChart data={dashboardData.monthlyChart} options={chartOptions} onClick={chartHandlers.handleMonthlyChartClick} height="h-[300px]" />
    </ChartCard>

    <ChartCard title="Performance Insights" subtitle="Business health overview" className="col-span-12 lg:col-span-3">
      <QuickStats stats={dashboardData.stats} />
    </ChartCard>
  </>
));

const TrendsSection = memo(({ dashboardData, chartHandlers, filters }) => (
  <>
    <SectionLabel color="bg-sky-500" title="Trends" hint="Year-over-year comparison" />

    <ChartCard title="Revenue Comparison" subtitle={`${filters.year} vs ${filters.compareYear}`} className="col-span-12 lg:col-span-6" insight="YoY">
      {dashboardData.revenueComparisonChart && (
        <div className="h-56">
          <Line data={dashboardData.revenueComparisonChart} options={chartOptions} />
        </div>
      )}
    </ChartCard>

    <ChartCard title="Customer Growth" subtitle="New registrations over time · click to view users" className="col-span-12 lg:col-span-6" insight="Click to explore">
      {dashboardData.userComparisonChart ? (
        <ClickableLineChart data={dashboardData.userComparisonChart} options={chartOptions} onClick={chartHandlers.handleUserChartClick} tooltipLabel="Click to view users" />
      ) : (
        <div className="h-56 flex items-center justify-center text-gray-400 text-sm">No data available</div>
      )}
    </ChartCard>
  </>
));

const GeographySection = memo(({ dashboardData, chartHandlers }) => (
  <>
    <SectionLabel color="bg-emerald-500" title="Geography" hint="City-wise breakdown · click bars to drill in" />

    <ChartCard title="Orders by City" subtitle="Total orders per location" className="col-span-12 lg:col-span-6" insight="Click to view orders">
      <ClickableBarChart data={dashboardData.cityChart} options={cityBarOptions} onClick={chartHandlers.handleCityChartClick} height="h-56" />
    </ChartCard>

    <ChartCard title="Users by City" subtitle="Registered users per location" className="col-span-12 lg:col-span-6" insight="Click to view users">
      <ClickableBarChart data={dashboardData.cityUsersChart} options={cityBarOptions} onClick={chartHandlers.handleCityUsersChartClick} height="h-56" />
    </ChartCard>
  </>
));

const RetentionSection = memo(() => (
  <>
    <SectionLabel color="bg-violet-500" title="Customer Retention" hint="Repeat vs new customers · click bars to view lists" />
    <RepeatRateChart />
  </>
));

// ── Main Dashboard ────────────────────────────────────────────
const AnalyticsDashboard = () => {
  const [filters, setFilters] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return {
      status: '',
      paymentMode: '',
      year,
      compareYear: year - 1,
      startDate: `${year}-${month}-01`,
      endDate: now.toISOString().split('T')[0],
    };
  });

  const [showFilters, setShowFilters] = useState(false);
  const [userModal, setUserModal] = useState(CLOSED_MODAL);
  const [orderModal, setOrderModal] = useState(CLOSED_MODAL);

  const { loading, dashboardData } = useAnalyticsData(filters);
  const compareYearOptions = useMemo(() => getCompareYearOptions(), []);
  const filterHandlers = useFilterHandlers(setFilters);
  const chartHandlers = useChartHandlers(filters, dashboardData, setOrderModal, setUserModal);

  const handleFilterClick = useCallback(() => setShowFilters(true), []);
  const handleCloseUserModal = useCallback(() => setUserModal(CLOSED_MODAL), []);
  const handleCloseOrderModal = useCallback(() => setOrderModal(CLOSED_MODAL), []);

  // Derived values computed once here, passed down as primitives
  const { stats } = dashboardData;
  const total = stats.delivered + stats.running + stats.cancelled || 1;
  const successRate = useMemo(() => ((stats.delivered / total) * 100).toFixed(1), [stats.delivered, total]);
  const upiPct = useMemo(() => Math.round((stats.upi / (stats.upi + stats.cod || 1)) * 100), [stats.upi, stats.cod]);

  return (
    <div className="min-h-screen bg-[#f4f5f9]">
      <DashboardHeader onFilterClick={handleFilterClick} />

      <FilterModal
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        filters={filters}
        setFilters={setFilters}
        {...filterHandlers}
        compareYearOptions={compareYearOptions}
      />

      {loading && <LoadingOverlay />}

      <div className="px-5 py-4">
        <div className="grid grid-cols-12 gap-4">

          <MetricsCards stats={stats} />

          <OperationsSection
            dashboardData={dashboardData}
            chartHandlers={chartHandlers}
            filters={filters}
            successRate={successRate}
            upiPct={upiPct}
          />

          <TrendsSection
            dashboardData={dashboardData}
            chartHandlers={chartHandlers}
            filters={filters}
          />

          <GeographySection
            dashboardData={dashboardData}
            chartHandlers={chartHandlers}
          />

          <RetentionSection />

        </div>
      </div>

      <UserListModal isOpen={userModal.isOpen} onClose={handleCloseUserModal} users={userModal.users} month={userModal.month} year={userModal.year} />
      <OrderListModal isOpen={orderModal.isOpen} onClose={handleCloseOrderModal} orders={orderModal.orders} month={orderModal.month} year={orderModal.year} />
    </div>
  );
};

export default AnalyticsDashboard;
